import React, { useState } from 'react';
import { View, Text, Pressable } from '../../lib/react-native-compat';
import JobMilestones, { useJobMilestones } from './JobMilestones';

// Project calendar on the report list: the job's Key Milestones (when the
// Overview tab has any) sit at the top of the same card, then up to three
// months side by side (flex-wrap stacks them on phones), arrows shift the
// window one month at a time, capped at the current month. Days with a saved
// report highlight by status (sky draft / green submitted) and open that
// report on tap. Milestone dates carry a small amber diamond; hovering the
// day (or tapping it on touch, when there's no report to open) pops the
// milestone names in a tooltip.

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];
const DOW = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

const STATUS_BG = { Submitted: '#059669', Draft: '#0284C7' };
const MILESTONE_COLOR = '#D97706';

const pad2 = (n) => String(n).padStart(2, '0');
const keyOf = (y, m, d) => `${y}-${pad2(m + 1)}-${pad2(d)}`;

const CELL = 30;

const MonthGrid = ({ year, month, byDate, milestonesByDate, todayKey, onPick }) => {
    // Date key of the cell whose milestone tooltip is open (hover or tap).
    const [tipKey, setTipKey] = useState(null);
    const firstDow = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells = [
        ...Array.from({ length: firstDow }, () => null),
        ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
    ];
    while (cells.length % 7 !== 0) cells.push(null);
    const weeks = [];
    for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));

    return (
        <View style={{ width: CELL * 7 }}>
            <Text style={{ fontSize: 13, fontWeight: '700', color: '#0F172A', textAlign: 'center', marginBottom: 6 }}>
                {`${MONTH_NAMES[month]} ${year}`}
            </Text>
            <View style={{ flexDirection: 'row' }}>
                {DOW.map((d, i) => (
                    <Text key={i} style={{ width: CELL, fontSize: 10, fontWeight: '600', color: '#94A3B8', textAlign: 'center' }}>{d}</Text>
                ))}
            </View>
            {weeks.map((week, wi) => (
                <View key={wi} style={{ flexDirection: 'row' }}>
                    {week.map((day, di) => {
                        if (!day) return <View key={di} style={{ width: CELL, height: CELL }} />;
                        const dateKey = keyOf(year, month, day);
                        const report = byDate[dateKey];
                        const milestoneNames = milestonesByDate[dateKey];
                        const isToday = dateKey === todayKey;
                        // Hover always shows the tooltip; tap only toggles it
                        // when there's no report — a report tap keeps opening
                        // the report.
                        const tipHandlers = milestoneNames ? {
                            onMouseEnter: () => setTipKey(dateKey),
                            onMouseLeave: () => setTipKey(null),
                        } : {};
                        return (
                            <Pressable
                                key={di}
                                title={report ? `#${report.ReportNumber} — ${report.Status}` : undefined}
                                onPress={report
                                    ? () => onPick(report)
                                    : milestoneNames
                                        ? () => setTipKey((k) => (k === dateKey ? null : dateKey))
                                        : undefined}
                                {...tipHandlers}
                                style={{
                                    width: CELL, height: CELL, alignItems: 'center', justifyContent: 'center',
                                    cursor: report || milestoneNames ? 'pointer' : 'default',
                                    position: 'relative',
                                }}
                            >
                                <View style={{
                                    width: CELL - 4, height: CELL - 4, borderRadius: 6,
                                    alignItems: 'center', justifyContent: 'center',
                                    backgroundColor: report ? (STATUS_BG[report.Status] || STATUS_BG.Draft) : 'transparent',
                                    border: isToday ? '2px solid #1D4ED8' : 'none',
                                }}
                                >
                                    <Text style={{
                                        fontSize: 11,
                                        fontWeight: report || isToday ? '700' : '400',
                                        color: report ? '#fff' : isToday ? '#1D4ED8' : '#334155',
                                        fontVariantNumeric: 'tabular-nums',
                                    }}
                                    >
                                        {day}
                                    </Text>
                                </View>
                                {milestoneNames && (
                                    <View style={{
                                        position: 'absolute', bottom: 3, left: '50%',
                                        width: 5, height: 5,
                                        backgroundColor: report ? '#fff' : MILESTONE_COLOR,
                                        transform: 'translateX(-50%) rotate(45deg)',
                                    }}
                                    />
                                )}
                                {milestoneNames && tipKey === dateKey && (
                                    <View style={{
                                        position: 'absolute', bottom: CELL + 2, left: '50%',
                                        transform: 'translateX(-50%)',
                                        backgroundColor: '#0F172A', borderRadius: 6,
                                        paddingTop: 4, paddingBottom: 4, paddingLeft: 8, paddingRight: 8,
                                        width: 'max-content', maxWidth: 220, zIndex: 20,
                                        pointerEvents: 'none',
                                    }}
                                    >
                                        {milestoneNames.map((name, ni) => (
                                            <Text key={ni} style={{ fontSize: 11, color: '#fff', textAlign: 'center' }}>{name}</Text>
                                        ))}
                                    </View>
                                )}
                            </Pressable>
                        );
                    })}
                </View>
            ))}
        </View>
    );
};

const ReportCalendar = ({ reports, onPick }) => {
    const now = new Date();
    const todayKey = keyOf(now.getFullYear(), now.getMonth(), now.getDate());
    // Anchor = rightmost month shown; the window is anchor-2 .. anchor.
    const [anchor, setAnchor] = useState({ y: now.getFullYear(), m: now.getMonth() });
    const milestoneData = useJobMilestones();

    // First report per date wins (dates are expected unique per job anyway).
    const byDate = {};
    for (const r of reports || []) {
        const k = String(r.ReportDate).slice(0, 10);
        if (!byDate[k]) byDate[k] = r;
    }

    // Milestone names per date for the diamond markers. Both sources mark
    // their current date (forecast, else baseline). Same name on the same
    // day from both sources collapses to one entry.
    const milestonesByDate = {};
    const addMilestone = (iso, name) => {
        const k = String(iso || '').slice(0, 10);
        if (!/^\d{4}-\d{2}-\d{2}$/.test(k)) return;
        if (!milestonesByDate[k]) milestonesByDate[k] = [];
        const label = name || 'Milestone';
        if (!milestonesByDate[k].includes(label)) milestonesByDate[k].push(label);
    };
    for (const m of milestoneData.milestones) addMilestone(m.forecast || m.baseline, m.name);
    for (const m of milestoneData.scheduleMilestones) addMilestone(m.forecast || m.baseline, m.name);

    const shift = (delta) => setAnchor(({ y, m }) => {
        const d = new Date(y, m + delta, 1);
        return { y: d.getFullYear(), m: d.getMonth() };
    });
    const atCurrentMonth = anchor.y === now.getFullYear() && anchor.m === now.getMonth();

    const months = [2, 1, 0].map((back) => {
        const d = new Date(anchor.y, anchor.m - back, 1);
        return { year: d.getFullYear(), month: d.getMonth() };
    });

    const arrowStyle = (disabled) => ({
        minWidth: 36, minHeight: 36, alignItems: 'center', justifyContent: 'center',
        borderRadius: 8, border: '1px solid #CBD5E1', backgroundColor: '#fff',
        cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.35 : 1,
    });

    return (
        <View style={{ borderRadius: 12, backgroundColor: '#fff', border: '1px solid #E2E8F0', padding: 14, marginBottom: 18 }}>
            <JobMilestones embedded data={milestoneData} />
            <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                <Pressable onPress={() => shift(-1)} title="Earlier month" style={arrowStyle(false)}>
                    <Text style={{ fontSize: 16, color: '#334155' }}>‹</Text>
                </Pressable>
                <Text style={{ fontSize: 13, fontWeight: '700', color: '#0F172A' }}>Report Calendar</Text>
                <Pressable onPress={atCurrentMonth ? undefined : () => shift(1)} title="Later month" style={arrowStyle(atCurrentMonth)}>
                    <Text style={{ fontSize: 16, color: '#334155' }}>›</Text>
                </Pressable>
            </View>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'center', columnGap: 18, rowGap: 14 }}>
                {months.map(({ year, month }) => (
                    <MonthGrid
                        key={`${year}-${month}`}
                        year={year}
                        month={month}
                        byDate={byDate}
                        milestonesByDate={milestonesByDate}
                        todayKey={todayKey}
                        onPick={onPick}
                    />
                ))}
            </View>
            <View style={{ flexDirection: 'row', justifyContent: 'center', columnGap: 16, marginTop: 10 }}>
                {[['Draft', STATUS_BG.Draft], ['Submitted', STATUS_BG.Submitted]].map(([label, color]) => (
                    <View key={label} style={{ flexDirection: 'row', alignItems: 'center', columnGap: 5 }}>
                        <View style={{ width: 10, height: 10, borderRadius: 3, backgroundColor: color }} />
                        <Text style={{ fontSize: 11, color: '#64748B' }}>{label}</Text>
                    </View>
                ))}
                <View style={{ flexDirection: 'row', alignItems: 'center', columnGap: 5 }}>
                    <View style={{ width: 10, height: 10, borderRadius: 3, border: '2px solid #1D4ED8', backgroundColor: '#fff' }} />
                    <Text style={{ fontSize: 11, color: '#64748B' }}>Today</Text>
                </View>
                {Object.keys(milestonesByDate).length > 0 && (
                    <View style={{ flexDirection: 'row', alignItems: 'center', columnGap: 5 }}>
                        <View style={{ width: 7, height: 7, backgroundColor: MILESTONE_COLOR, transform: 'rotate(45deg)' }} />
                        <Text style={{ fontSize: 11, color: '#64748B' }}>Milestone</Text>
                    </View>
                )}
            </View>
        </View>
    );
};

export default ReportCalendar;
